import { AccountPlan } from 'stores/account/overviewStore/model/interfaces';
import {
  ChartComputedData,
  defaultChartData,
  Portfolio,
} from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';

import { API_ACCOUNT_DETAIL_OVERVIEW } from 'api/endpoints';
import { action, observable } from 'mobx';
import { computeChartData } from 'helpers/chart';
import { ClassesAsset } from 'common/interfaces';

import { RootStore } from 'stores/rootStore';
import { Flows } from 'pages/Accounts/Detail/enums';
import { fingerprint } from 'helpers/helpers';

import { Bucket } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import { AmountValue } from 'common/interfaces';

const missingAsset = { categoryIndex: 0, classIndex: 0, assetIndex: 0, asset: undefined };

function findCategoryShare(assetCategoryCode: string, portfolio: Portfolio) {
  return portfolio.assetCategories.find((category) => category.code === assetCategoryCode)?.share || 0;
}

function findAsset(
  assetCategoryCode: string,
  assetClassCode: string,
  assetCode: string,
  portfolio: Portfolio,
): { categoryIndex: number; classIndex: number; assetIndex: number; asset?: ClassesAsset } {
  const categoryIndex = portfolio.assetCategories.findIndex((category) => category.code === assetCategoryCode);
  if (categoryIndex < 0) return missingAsset;
  const classIndex = portfolio.assetCategories[categoryIndex].assetClasses.findIndex(
    (assetClass) => assetClass.code === assetClassCode,
  );
  if (classIndex < 0) return missingAsset;
  const assetIndex = portfolio.assetCategories[categoryIndex].assetClasses[classIndex].assets
    ? portfolio.assetCategories[categoryIndex].assetClasses[classIndex].assets!.findIndex(
        (asset) => asset.code === assetCode,
      )
    : -1;
  return assetIndex < 0
    ? missingAsset
    : {
        categoryIndex,
        classIndex,
        assetIndex,
        asset: portfolio.assetCategories[categoryIndex].assetClasses[classIndex].assets![assetIndex],
      };
}

export interface Comparison {
  assetCategories: {
    code: string;
    title: string;
    share: number;
    proposedShare: number;
    assetClasses: {
      code: string;
      assets: {
        code: string;
        title: string;
        share: number;
        proposedShare: number;
      }[];
    }[];
  }[];
}

export interface PortfolioChange {
  LONG_TERM: boolean;
  MID_TERM: boolean;
  SHORT_TERM: boolean;
}

export function makePortfolioComparison(originalBuckets?: Partial<Bucket>[], proposedBuckets?: Partial<Bucket>[]): Comparison[] {
  if (!originalBuckets || !proposedBuckets) return [];
  if (!originalBuckets.every(b => !!b.portfolio) || !proposedBuckets.every(b => !!b.portfolio)) return [];
  const comparison: Comparison[] = originalBuckets.map((bucket, bucketIndex) => {
    return {
      assetCategories: bucket.portfolio!.assetCategories.map((assetCategory) => {
        return {
          share: assetCategory.share,
          proposedShare: findCategoryShare(assetCategory.code, proposedBuckets[bucketIndex].portfolio!),
          code: assetCategory.code,
          title: assetCategory.title,
          assetClasses: assetCategory.assetClasses.map((assetClass) => {
            return {
              code: assetClass.code,
              assets: assetClass.assets
                ? assetClass.assets?.map((asset) => {
                    const share = asset.share;
                    const proposedAsset = findAsset(
                      assetCategory.code,
                      assetClass.code,
                      asset.code,
                      proposedBuckets[bucketIndex].portfolio!,
                    );
                    if (proposedAsset.asset !== undefined) {
                      proposedBuckets[bucketIndex].portfolio!.assetCategories[proposedAsset.categoryIndex].assetClasses[
                        proposedAsset.classIndex
                      ].assets!.splice(proposedAsset.assetIndex, 1);
                    }
                    return {
                      code: asset.code,
                      title: asset.title,
                      share: share,
                      proposedShare: proposedAsset.asset?.share || 0,
                    };
                  })
                : [],
            };
          }),
        };
      }),
    };
  });

  proposedBuckets.forEach((bucket, bucketIndex) => {
    bucket.portfolio!.assetCategories.forEach((assetCategory) => {
      let categoryIndex = comparison[bucketIndex].assetCategories.findIndex(
        (oAssetCategory) => oAssetCategory.code === assetCategory.code,
      );
      if (categoryIndex < 0) {
        comparison[bucketIndex].assetCategories.push({
          code: assetCategory.code,
          title: assetCategory.title,
          share: 0,
          proposedShare: assetCategory.share,
          assetClasses: [],
        });
        categoryIndex = comparison[bucketIndex].assetCategories.length - 1;
      }
      assetCategory.assetClasses.forEach((assetClass) => {
        let classIndex = comparison[bucketIndex].assetCategories[categoryIndex].assetClasses.findIndex(
          (oClass) => oClass.code === assetClass.code,
        );
        if (classIndex < 0) {
          comparison[bucketIndex].assetCategories[categoryIndex].assetClasses.push({
            code: assetClass.code,
            assets: [],
          });
          classIndex = comparison[bucketIndex].assetCategories[categoryIndex].assetClasses.length - 1;
        }
        assetClass.assets?.forEach((asset) => {
          comparison[bucketIndex].assetCategories[categoryIndex].assetClasses[classIndex].assets.push({
            share: 0,
            title: asset.title,
            code: asset.code,
            proposedShare: asset.share,
          });
        });
      });
    });
    comparison[bucketIndex].assetCategories = comparison[bucketIndex].assetCategories
      .slice()
      .sort((a: any, b: any) => a.code.localeCompare(b.code));
  });

  return comparison;
}

export function hasTransfers(flow?: Flows | null, transfers?: { [key: string]: AmountValue }) {
  return !flow || !transfers
    ? false
    : flow === Flows.PLAN
    ? Object.values(transfers).some((t) => t.value !== 0)
    : Object.entries(transfers).some((pair) => !pair[0].includes('CASH') && pair[1].value !== 0);
}

export function hastTansfersFromCashOnly(transfers?: { [key: string]: AmountValue }) {
  return !transfers
    ? false
    : !Object.entries(transfers).some((pair) => pair[0].split('_')[1] === 'CASH' && pair[1].value !== 0);
}

export function findPortfolioChanges(originalBuckets?: Bucket[], proposedBuckets?: Bucket[]) {
  if (!originalBuckets || !proposedBuckets) return { LONG_TERM: false, MID_TERM: false, SHORT_TERM: false };
  return {
    LONG_TERM: fingerprint(originalBuckets[2].portfolio) !== fingerprint(proposedBuckets[2].portfolio),
    MID_TERM: fingerprint(originalBuckets[1].portfolio) !== fingerprint(proposedBuckets[1].portfolio),
    SHORT_TERM: fingerprint(originalBuckets[0].portfolio) !== fingerprint(proposedBuckets[0].portfolio),
  };
}

export class OverviewStore {
  @observable data: AccountPlan | null = null;
  @observable riskChange = false;
  accountId = '';
  flow: Flows | null = null;
  rootStore: RootStore;

  @observable detailMode = true;

  @observable originalChartData: ChartComputedData = defaultChartData;
  @observable proposedChartData: ChartComputedData = defaultChartData;
  @observable comparison: Comparison[] = [];
  @observable transfers = false;
  @observable transfersFromCashOnly = false;
  @observable withdrawAll = false;
  @observable portfolioChange: PortfolioChange = { LONG_TERM: false, MID_TERM: false, SHORT_TERM: false };

  constructor(rootStore: RootStore, accountId: string, flow: Flows) {
    this.rootStore = rootStore;
    this.accountId = accountId;
    this.flow = flow;
  }

  @action
  setAccountId = (accountId: string) => (this.accountId = accountId);

  @action
  fetchOverview = async () => {
    const response = await this.rootStore.stores.apiStore.get<AccountPlan>(API_ACCOUNT_DETAIL_OVERVIEW(this.accountId));
    if (response?.data) {
      computeChartData(response.data.original, this.originalChartData);
      computeChartData(response.data.proposed, this.proposedChartData);
      this.transfers = hasTransfers(this.flow, response.data.transfers);
      this.transfersFromCashOnly = hastTansfersFromCashOnly(response.data.transfers);
      this.withdrawAll = this.flow === Flows.WITHDRAWAL && !response.data.withdrawal;
      this.portfolioChange = findPortfolioChanges(response.data.original.buckets, response.data.proposed.buckets);
      this.comparison = makePortfolioComparison(response.data.original.buckets, response.data.proposed.buckets);
      this.data = response.data;
    }
  };

  handleNext = () => {};
  handleBack = () => {};
}
